import { getErrorMessage } from '@/lib/error';
import type { FormInstance } from 'antd';
import { useEffect, useImperativeHandle, type Ref } from 'react';
import type { ConfigPanelHandle } from './salary-grade.types';

type Options<TData, TValues extends object> = {
  form: FormInstance;
  /** Latest server payload, or undefined while the query is loading. */
  data?: TData;
  /** Server payload to form shape. Keep it a module level function. */
  toValues: (data: TData) => TValues;
  /** Page level edit mode. */
  isEditing: boolean;
  /** Sends the payload. Must throw on failure, i.e. an unwrapped mutation. */
  submit: (values: TValues) => Promise<unknown>;
  /** Fallback message when the server sends none. */
  errorMessage: string;
  /** A rule antd cannot express, e.g. "components must total 100%". */
  extraValidation?: (values: TValues) => string | null;
};

/**
 * Everything the three configuration panels share: filling the form from the
 * server, and exposing validate / save / reset to the page.
 *
 * Without this the same thirty lines would sit in each panel, so a change to
 * how saving reports back would have to be made three times.
 */
export const useConfigPanel = <TData, TValues extends object>(
  ref: Ref<ConfigPanelHandle> | undefined,
  {
    form,
    data,
    toValues,
    isEditing,
    submit,
    errorMessage,
    extraValidation,
  }: Options<TData, TValues>,
) => {
  // Server values fill the form, but never on top of unsaved edits.
  useEffect(() => {
    if (!isEditing && data) form.setFieldsValue(toValues(data));
  }, [data, form, isEditing, toValues]);

  useImperativeHandle(ref, () => ({
    validate: async () => {
      try {
        await form.validateFields();
      } catch {
        return 'please fix the highlighted fields';
      }
      return extraValidation?.(form.getFieldsValue(true)) ?? null;
    },
    save: async () => {
      try {
        await submit(form.getFieldsValue(true));
        return null;
      } catch (error) {
        return getErrorMessage(error, errorMessage);
      }
    },
    reset: () => {
      if (data) form.setFieldsValue(toValues(data));
    },
  }));
};
